import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';

import { AuthService } from '../../auth-pages/services/auth.service';
import { UserDetailModalComponent } from '../user-detail-modal/user-detail-modal.component';
import { UserApiService } from '../services/user-api.service';
import { CrmPageResponse, CrmUserResponse, CrmRoleResponse } from '../models/user.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    ModalComponent,
    HasPermissionDirective,
    UserDetailModalComponent
  ],
  templateUrl: './user-list.component.html'
})
export class UserListComponent implements OnInit {
  allUsers: CrmUserResponse[] = [];
  users: CrmUserResponse[] = [];

  loading = false;
  error = '';

  businessId!: number;
  currentRoleId!: number;
  currentUserId: number | null = null;

  canUseModule = true;
  isSuperAdmin = false;

  showFilters = false;
  filtroTerm = '';
  filtroActivo: boolean | undefined;
  filtroRoleId: number | undefined;

  sortBy: 'mail' | 'name' | 'role' | 'updated' = 'updated';
  sortDir: 'asc' | 'desc' = 'desc';

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'user-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 20;

  page = 1;
  totalFiltered = 0;
  totalPages = 1;

  get globalPage(): number { return this.page - 1; }
  get globalTotalPages(): number { return this.totalPages; }
  get globalTotalElements(): number { return this.totalFiltered; }
  get hasPrevPage(): boolean { return this.page > 1; }
  get hasNextPage(): boolean { return this.page < this.totalPages; }
  get pageDisplay(): number { return this.page; }
  get pagesArray(): number[] { return Array.from({ length: this.totalPages }, (_, i) => i + 1); }

  isDetailOpen = false;
  selectedUser: CrmUserResponse | null = null;

  roles: CrmRoleResponse[] = [];

  private readonly SEARCH_BATCH_SIZE = 2000;
  private businessNames = new Map<number, string>(); 

  isResetOpen = false;
  resetTargetUser: CrmUserResponse | null = null;
  resettingPassword = false;
  resetError = '';
  resetSuccess = '';

  //
  isDeleteOpen = false;
  deleteTargetUser: CrmUserResponse | null = null;
  deletePassword = '';
  deletingUserData = false;
  deleteError = '';

  constructor(
    private auth: AuthService,
    private api: UserApiService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const bId = this.auth.getBusinessId();
    this.loadBusinessNames(); 
    const cu = this.auth.getCurrentUser();

    this.businessId = bId ?? 0;
    this.currentRoleId = (cu as any)?.roleId ?? (cu as any)?.idRol ?? 999;
    this.currentUserId = Number((cu as any)?.id ?? (cu as any)?.userId ?? 0) || null;

    this.isSuperAdmin = this.currentRoleId === 1;
    this.canUseModule = this.currentRoleId <= 3;

    if (!this.canUseModule) {
      this.error = 'No tienes permisos para usar el módulo de usuarios.';
      return;
    }

    this.loadRoles();
    this.cargarUsers();
  }

  private loadRoles(): void {
    this.api.getAssignableRoles().subscribe({
      next: (res) => {
        this.roles = res ?? [];
      },
      error: () => {
        this.roles = [];
      }
    });
  }

  private loadBusinessNames(): void {
    this.api.getBusinesses(0, 1000).subscribe({
      next: (res) => {
        (res?.content ?? []).forEach((b: any) => {
          this.businessNames.set(Number(b.id), b.name ?? '');
          // ⚠️ ajusta 'name' si el campo es otro
        });
      },
      error: () => {}
    });
  }

  private needsClientFallback(): boolean {
    const term = this.filtroTerm.trim();
    return term.length > 0 || this.sortBy !== 'mail' || this.sortDir !== 'asc';
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  buscar(): void {
    this.page = 1;
    this.cargarUsers();
  }

  limpiarFiltro(): void {
    this.filtroTerm = '';
    this.filtroActivo = undefined;
    this.filtroRoleId = undefined;
    this.sortBy = 'updated';
    this.sortDir = 'desc';
    this.page = 1;
    this.cargarUsers();
  }

  toggleSortDir(): void {
    this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    this.page = 1;
    this.cargarUsers();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  cambiarPagina(page: number): void {
    const newPage = page + 1;
    if (newPage < 1 || newPage > this.totalPages) return;
    this.page = newPage;
    if (this.needsClientFallback()) this.applyClientFilters(false);
    else this.cargarUsers();
  }

  cargarUsers(): void {
    this.loading = true;
    this.error = '';

    if (this.needsClientFallback()) {
      this.api.searchUsers({
        businessId: this.businessId,
        isActive: this.filtroActivo ?? null,
        roleId: this.filtroRoleId ?? null,
        page: 0,
        size: this.SEARCH_BATCH_SIZE
      }).subscribe({
        next: (res: CrmPageResponse<CrmUserResponse>) => {
          this.allUsers = (res?.content ?? []).map(u => this.normalizeUser(u));
          this.applyClientFilters(true);
          this.loading = false;
        },
        error: (err) => {
          this.error = this.extractErrorMessage(err, 'Error al cargar usuarios');
          this.loading = false;
        }
      });
      return;
    }

    const backendPage = Math.max(0, this.page - 1);

    this.api.searchUsers({
      businessId: this.businessId,
      isActive: this.filtroActivo ?? null,
      roleId: this.filtroRoleId ?? null,
      page: backendPage,
      size: this.pageSize
    }).subscribe({
      next: (res: CrmPageResponse<CrmUserResponse>) => {
        this.allUsers = [];
        this.users = (res?.content ?? []).map(u => this.normalizeUser(u));
        this.totalFiltered = Number(res?.totalElements ?? this.users.length);
        this.totalPages = Math.max(1, Number(res?.totalPages ?? 1));
        if (this.page > this.totalPages) this.page = this.totalPages;
        if (this.page < 1) this.page = 1;
        this.loading = false;
      },
      error: (err) => {
        this.error = this.extractErrorMessage(err, 'Error al cargar usuarios');
        this.loading = false;
      }
    });
  }

  private applyClientFilters(resetPage: boolean = true): void {
    const base = [...this.allUsers];
    const term = this.filtroTerm.trim().toLowerCase();
    let filtered = base;

    if (term) {
      filtered = filtered.filter(u => {
        const mail = (u.mail ?? '').toLowerCase();
        const fullname = (u.fullname ?? '').toLowerCase();
        const lastname = (u.lastname ?? '').toLowerCase();
        const full = `${fullname} ${lastname}`.trim();
        return mail.includes(term) || fullname.includes(term) || lastname.includes(term) || full.includes(term);
      });
    }

    if (this.filtroActivo !== undefined) {
      filtered = filtered.filter(u => (u.isActive ?? false) === this.filtroActivo);
    }

    if (this.filtroRoleId !== undefined) {
      filtered = filtered.filter(u => Number(u.idRol ?? 0) === Number(this.filtroRoleId));
    }

    const dir = this.sortDir === 'asc' ? 1 : -1;

    filtered.sort((a, b) => {
      if (this.sortBy === 'updated') {
        const av = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bv = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return (av - bv) * dir;
      }

      if (this.sortBy === 'name') {
        const av = `${a.fullname ?? ''} ${a.lastname ?? ''}`.toLowerCase().trim();
        const bv = `${b.fullname ?? ''} ${b.lastname ?? ''}`.toLowerCase().trim();
        return av.localeCompare(bv) * dir;
      }

      if (this.sortBy === 'role') {
        const av = (this.roleLabel(a.idRol, a.roleName) ?? '').toLowerCase();
        const bv = (this.roleLabel(b.idRol, b.roleName) ?? '').toLowerCase();
        return av.localeCompare(bv) * dir;
      }

      const av = (a.mail ?? '').toLowerCase();
      const bv = (b.mail ?? '').toLowerCase();
      return av.localeCompare(bv) * dir;
    });

    this.totalFiltered = filtered.length;
    this.totalPages = Math.max(1, Math.ceil(this.totalFiltered / this.pageSize));

    if (resetPage) this.page = 1;
    this.page = Math.min(this.page, this.totalPages);
    if (this.page < 1) this.page = 1;

    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.users = filtered.slice(start, end);
  }

  create(): void {
    if (!this.canUseModule) return;
    this.router.navigate(['/users/crear']);
  }

  edit(u: CrmUserResponse): void {
    if (!this.isSuperAdmin) return;
    this.router.navigate([`/users/editar/${u.id}`]);
  }

  async deactivate(u: CrmUserResponse): Promise<void> {
    if (!this.isSuperAdmin) return;

    if (this.currentUserId && u.id === this.currentUserId) {
      await this.confirmDialog.alert({ title: 'Aviso', message: 'No puedes desactivar tu propia cuenta mientras estás usando el sistema.', variant: 'warning' });
      return;
    }

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Desactivar usuario',
      message: `Se desactivará a ${u.mail}. ¿Deseas continuar?`,
      // Sin esto el botón rotula 'Eliminar' (default de confirmDanger) en un
      // diálogo que solo desactiva. `activate` ya pasaba su propio texto.
      confirmText: 'Desactivar'
    });
    if (!ok) return;

    this.api.deactivateAsSuperAdmin(u.id).subscribe({
      next: () => {
        this.toast.success({ title: 'Usuario desactivado', message: `Se desactivó a ${u.mail} correctamente.` });
        if (this.page > 1 && this.users.length === 1) this.page--;
        this.cargarUsers();
      },
      error: (err) => this.toast.error({ title: 'Error', message: this.extractErrorMessage(err, 'No se pudo desactivar el usuario.') })
    });
  }

  async activate(u: CrmUserResponse): Promise<void> {
    if (!this.isSuperAdmin) return;

    if (this.currentUserId && u.id === this.currentUserId) {
      await this.confirmDialog.alert({ title: 'Aviso', message: 'No puedes activar tu propia cuenta desde esta sesión.', variant: 'warning' });
      return;
    }

    const ok = await this.confirmDialog.confirm({
      title: 'Activar usuario',
      message: `Se activará a ${u.mail}. ¿Deseas continuar?`,
      confirmText: 'Activar'
    });
    if (!ok) return;

    this.api.activateAsSuperAdmin(u.id).subscribe({
      next: () => {
      this.toast.success({ title: 'Usuario activado', message: `Se activó a ${u.mail} correctamente.` });   // ← agregar
      this.cargarUsers();
    },
      error: (err) => this.toast.error({ title: 'Error', message: this.extractErrorMessage(err, 'No se pudo activar el usuario.') })
    });
  }

  async deleteUserData(u: CrmUserResponse): Promise<void> {
    if (!this.isSuperAdmin) return;

    if (this.currentUserId && u.id === this.currentUserId) {
      await this.confirmDialog.alert({ title: 'Aviso', message: 'No puedes eliminar tus propios datos desde este panel. Usa la Zona de peligro en Configuración de cuenta.', variant: 'warning' });
      return;
    }

    if ((u.isActive ?? true) !== false) {
      await this.mostrarAvisoDebeEstarInactivo();
      return;
    }

    this.deleteTargetUser = u;
    this.deletePassword = '';
    this.deleteError = '';
    this.isDeleteOpen = true;
  }

  closeDeleteUserData(): void {
    this.isDeleteOpen = false;
    this.deleteTargetUser = null;
    this.deletePassword = '';
    this.deleteError = '';
    this.deletingUserData = false;
  }

  confirmDeleteUserData(): void {
    const target = this.deleteTargetUser;
    if (!target || !this.deletePassword) return;

    this.deleteError = '';
    this.deletingUserData = true;

    this.api.deleteUserDataAsSuperAdmin(target.id, this.deletePassword)
      .pipe(finalize(() => (this.deletingUserData = false)))
      .subscribe({
        next: () => {
          this.toast.deleted('Datos del usuario');
          this.closeDeleteUserData();
          this.cargarUsers();
        },
        error: (err) => {
          if (err?.status === 401) {
            this.deleteError = 'Contraseña incorrecta.';
            return;
          }
          this.deleteError = this.extractErrorMessage(err, 'No se pudieron eliminar los datos del usuario.');
        }
      });
  }

  verUser(u: CrmUserResponse): void {
    this.selectedUser = this.normalizeUser({ ...u });
    this.isDetailOpen = true;
  }

  cerrarDetalle(): void {
    this.isDetailOpen = false;
    this.selectedUser = null;
  }

  /**
   * Nombre del negocio del usuario abierto en el detalle. El modal prefiere el
   * `businessName` que venga en su propia relectura por id; esto es el respaldo, resuelto
   * con el catálogo de negocios que ya carga esta lista.
   */
  get selectedUserBusinessName(): string {
    const u = this.selectedUser;
    if (!u) return '';
    return this.businessLabel(u.businessId, u.businessName);
  }

  canResetPassword(u: CrmUserResponse): boolean {
    if (!(this.currentRoleId === 1 || this.currentRoleId === 2)) return false;
    if (!this.currentUserId) return false;
    if (u.id === this.currentUserId) return false;

    if (this.currentRoleId === 2) {
      if (Number(u.businessId) !== Number(this.businessId)) return false;
      if (Number(u.idRol) <= 2) return false;
    }

    return true;
  }

  openResetPassword(u: CrmUserResponse, ev?: MouseEvent): void {
    if (ev) ev.stopPropagation();
    if (!this.canResetPassword(u)) return;

    this.resetTargetUser = this.normalizeUser({ ...u });
    this.resetError = '';
    this.resetSuccess = '';
    this.isResetOpen = true;
  }

  closeResetPassword(): void {
    this.isResetOpen = false;
    this.resetTargetUser = null;
    this.resetError = '';
    this.resetSuccess = '';
    this.resettingPassword = false;
  }

  // INC_CRM_022 inc5: en lugar de fijar una contraseña, se envía al usuario un enlace
  // seguro de restablecimiento con expiración para que él mismo defina su nueva contraseña.
  sendResetLink(): void {
    this.resetError = '';
    this.resetSuccess = '';

    const target = this.resetTargetUser;
    if (!target) return;

    this.resettingPassword = true;

    this.api.sendResetLinkAsAdmin(target.id)
      .pipe(finalize(() => (this.resettingPassword = false)))
      .subscribe({
        next: () => {
          this.resetSuccess = `Se envió un enlace de restablecimiento a ${target.mail}.`;
          this.toast.success({
            title: 'Enlace enviado',
            message: `El usuario recibirá un correo con un enlace seguro para restablecer su contraseña.`
          });
          setTimeout(() => this.closeResetPassword(), 1200);
        },
        error: (err) => {
          const status = err?.status;

          if (status === 403) {
            this.resetError = 'No tienes permisos para restablecer la contraseña de este usuario.';
            return;
          }
          if (status === 404) {
            this.resetError = 'Usuario no encontrado.';
            return;
          }
          if (status === 400) {
            this.resetError = 'El usuario no tiene un correo válido para recibir el enlace.';
            return;
          }

          this.resetError = 'No se pudo enviar el enlace de restablecimiento. Intenta nuevamente.';
        }
      });
  }

  getEstadoLabel(u: CrmUserResponse): string {
    return (u.isActive ?? false) ? 'Activo' : 'Inactivo';
  }

  getEstadoColor(u: CrmUserResponse): 'success' | 'error' {
    return (u.isActive ?? false) ? 'success' : 'error';
  }

  roleLabel(idRol: number, roleName: string): string {
    const n = roleName?.trim();
    if (n) return n;
    return this.roles.find(r => r.id === idRol)?.name ?? `Rol ${idRol}`;
  }

  businessLabel(businessId: number | null | undefined, businessName?: string | null): string {
    // INC_CRM_022 inc6: preferir el nombre real del negocio. Prioridad:
    // 1) el que trae el propio usuario (backend), 2) el catálogo de negocios, 3) fallback.
    if (businessName?.trim()) return businessName.trim();
    const id = Number(businessId ?? 0);
    if (!id) return '-';
    const name = this.businessNames.get(id);
    return name?.trim() ? name : `Empresa #${id}`;
  }

  private normalizeUser(u: any): CrmUserResponse {
    return {
      id: Number(u?.id ?? 0),
      fullname: u?.fullname ?? '',
      lastname: u?.lastname ?? '',
      mail: u?.mail ?? '',
      businessId: Number(u?.businessId ?? this.businessId ?? 0),
      businessName: u?.businessName ?? null,
      idRol: Number(u?.idRol ?? 0),
      roleName: u?.roleName ?? '',
      countryAlphabeticCode: u?.countryAlphabeticCode ?? null,
      isActive: u?.isActive ?? null,
      createdAt: u?.createdAt ?? null,
      updatedAt: u?.updatedAt ?? null,
      avatarFileId: u?.avatarFileId ?? null,
      avatarUrl: u?.avatarUrl ?? null
    };
  }

  private extractErrorMessage(err: any, fallback: string): string {
    const raw =
      err?.error?.message ||
      err?.error?.error ||
      err?.error?.detail ||
      (typeof err?.error === 'string' ? err.error : '') ||
      err?.message ||
      '';

    const msg = String(raw).toLowerCase();
    const status = err?.status;

    if (status === 0) return 'No se pudo conectar al servidor. Revisa tu conexión.';
    if (status === 400) return 'Revisa los datos ingresados. Hay campos inválidos.';
    if (status === 401) return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    if (status === 403) return 'No tienes permisos para realizar esta acción.';
    if (status === 404) return 'No se encontró el recurso solicitado.';
    if (status >= 500) return 'Ocurrió un error en el servidor. Intenta nuevamente.';

    if (msg.includes('23505') || msg.includes('duplicate') || msg.includes('llave duplicada')) {
      if (msg.includes('mail') || msg.includes('email')) return 'El correo ya está registrado. Usa otro.';
      return 'Ya existe un registro con esos datos. Revisa e intenta otra vez.';
    }

    return raw || fallback;
  }

  //
  async mostrarAvisoDebeEstarInactivo(): Promise<void> {
    await this.confirmDialog.alert({
      title: 'Acción no permitida',
      message: 'Para eliminar los datos de este usuario, primero debe cambiarse su estado a Inactivo.',
      variant: 'warning'
    });
  }
}